import { Injectable } from "@angular/core";
import { GlobalService } from "app/global/global.service";
import { ConditioanlsFeilds, SelectedRole } from "../general-popover-settings.dto";
import { Field } from "app/interfaces/fields";
import { applyRelationsOnCurrent } from "app/global/fieldrelation";

@Injectable({
    providedIn: 'root'
})

export class ConditionalService {

    constructor(
        private globalService: GlobalService,
    ) { }

    /**
     * Fetches and processes conditional fields based on the provided UUID and selected roles.
     *
     * @param currentUuid - The UUID or numeric identifier of the current entity.
     * @param selectedRoles - An array of selected roles used to filter or map the fields.
     * @returns An array of conditional fields after filtering and mapping operations.
     */
    fetchConditionalFields(currentUuid: string | number, selectedRoles: SelectedRole[]) {
        const fieldsData = this.globalService.filterDuplicates(this.globalService.fields_data);
        return Object.values(fieldsData)
            .filter(field => this.shouldIncludeField(field, currentUuid))
            .reduce((acc, field) => acc.concat(this.mapToConditionalFields(field, selectedRoles)), []);
    }

    /**
     * Determines whether a given field should be included based on specific conditions.
     *
     * @param field - The field object to evaluate.
     * @param currentUuid - The UUID of the current field to exclude from evaluation.
     * @returns A boolean indicating whether the field meets the inclusion criteria:
     * - The field is defined.
     * - The field's UUID is not equal to the current UUID.
     * - A DOM element with the class `drag-fields` and the field's UUID exists.
     * - The field's `selected_field` value is one of "1", "2", "3", "4", or "6".
     */
    private shouldIncludeField(field: Field, currentUuid: string | number): boolean {
        const currentField:Field = this.globalService.fields_data[currentUuid];
        return (
            field &&
            field.uuid !== currentUuid &&
            (field.selected_field_type_id !== currentField.selected_field_type_id || Number(field.selected_field_type_id) === 0) &&
            (document.querySelector(`.drag-fields[uuid="${field.uuid}"]`) || field?.semi_hidden_field === 1) &&
            ["1", "2", "3", "4", "6"].includes(field.selected_field)
        );
    }

    /**
     * Maps a given field and its associated roles to a structured format for conditional fields.
     * 
     * @param field - The field object containing details such as `uuid`, `selected_for_role_id`, 
     *                `selected_field`, `options`, `select_type_text`, and `type`.
     * @param selectedRoles - An array of roles, where each role contains an `id` and `name`.
     * 
     * @returns An array of objects representing the mapped conditional fields. Each object includes:
     *          - `uuid`: A unique identifier for the field or option.
     *          - `name`: The name of the field or option.
     *          - `role_name`: The name of the role associated with the field.
     *          - `field_name`: The text representation of the field type.
     *          - `selected_field`: The identifier of the selected field.
     *          - `main_uuid`: The main unique identifier of the field.
     *          - `type`: The type of the field.
     */
    private mapToConditionalFields(field: Field, selectedRoles: SelectedRole[]) {
        const role = selectedRoles.find(r =>
            r.id == field.selected_for_role_id || r.name === String(field.selected_for_role_id)
        );
        field.role_name = role?.name || "";

        if (["3", "6"].includes(field.selected_field) && Array.isArray(field.options)) {
            return field.options
                .filter(option => option?.name)
                .map(option => ({
                    uuid: `${field.uuid}${option.id}`,
                    name: option.name,
                    role_name: field.role_name,
                    field_name: field.select_type_text,
                    selected_field: field.selected_field,
                    main_uuid: field.uuid,
                    type: field.type
                }));
        }

        return [{
            uuid: field.uuid,
            name: field.select_type_text,
            role_name: field.role_name,
            selected_field: field.selected_field,
            main_uuid: field.uuid,
            type: field.type
        }];
    }

    activeConditionalField(enable_conditional: number, fieldData: Field) {
        this.globalService.updateField(fieldData.uuid, {
            enable_conditional: enable_conditional,
        });
        
        // Clear parent references from connected fields
        fieldData.connected_parent_conditional_id.forEach(parent => {
            const selectedUuid = ['droplist', 'list'].includes(parent.type) ? parent.main_uuid : parent.uuid;
            const selectedField: Field = this.globalService.fields_data[selectedUuid];
            if(selectedField?.linked_conditional_id){
                const updatedLinkedIds = selectedField.linked_conditional_id.filter(id => id !== fieldData.uuid);
                this.globalService.updateField(selectedUuid, {
                    linked_conditional_id: updatedLinkedIds,
                });
            }
        });

        // Reset current field’s conditional parent references
        this.globalService.updateField(fieldData.uuid, {
            connected_parent_conditional_id: [],
        });
        if(enable_conditional === 0){
            applyRelationsOnCurrent(fieldData.uuid, this.globalService, true);
        }
    }

    onSelectRelations(fields: ConditioanlsFeilds[], fieldData: Field) {
        const currentUuid = fieldData.uuid;
        const selectedFields = this.filterDuplicateFieldsForCondition(fields);
        // Add currentUuid as a dependency to selected fields
        selectedFields.forEach(field => this.addLinkedConditional(field, currentUuid));

        // If no fields selected, clean up old links
        if (fields.length === 0) {
            const matchingFields = this.getMatchingCurrentFields(fieldData) || [fieldData];
            matchingFields.forEach(field => this.removeOldLinks(field));
        }

        // Update current field's parent conditionals
        this.globalService.updateField(currentUuid, {
            connected_parent_conditional_id: selectedFields,
        });
        const updatedFieldData = this.globalService.fields_data[currentUuid];
        applyRelationsOnCurrent(currentUuid, this.globalService, true);
        this.syncMatchingConditionalFields(updatedFieldData);
        return updatedFieldData;
    }

    private addLinkedConditional(field: ConditioanlsFeilds, currentUuid: string) {
        const selectedUuid = ['droplist', 'list'].includes(field.type) ? field.main_uuid : field.uuid;
        const selectedField: Field = this.globalService.fields_data[selectedUuid];
        const linkedIds = selectedField.linked_conditional_id || [];

        if (!linkedIds.includes(currentUuid)) {
            linkedIds.push(currentUuid);
            this.globalService.updateField(selectedUuid, { linked_conditional_id: linkedIds });
        }
    }

    private getMatchingCurrentFields(fieldData: Field): Field[] {
        return this.globalService.findMatchingField(
            this.globalService.fields_data,
            "selected_field_type_id",
            fieldData.selected_field_type_id,
            "document_field"
        ) as Field[];
    }

    private removeOldLinks(field: Field) {
        field.connected_parent_conditional_id.forEach(parent => {
            const selectedUuid = ['droplist', 'list'].includes(parent.type) ? parent.main_uuid : parent.uuid;
            const selectedField: Field = this.globalService.fields_data[selectedUuid];
            const updatedIds = (selectedField.linked_conditional_id || []).filter(id => id !== field.uuid);
            this.globalService.updateField(selectedUuid, { linked_conditional_id: updatedIds });
        });
    }

    filterDuplicateFieldsForCondition(fields: ConditioanlsFeilds[]) {
        const result: ConditioanlsFeilds[] = [];

        fields.forEach(originalField => {
            const fieldData = this.globalService.fields_data[originalField.main_uuid];
            const matchingFields = this.globalService.findMatchingField(
                this.globalService.fields_data,
                "selected_field_type_id",
                fieldData.selected_field_type_id,
                "document_field"
            ) as Field[];
            matchingFields
                .filter((f: Field) => f.uuid !== fieldData.uuid && f.field_section !== "custom")
                .forEach((f: Field) =>
                    result.push({
                        uuid: f.uuid,
                        name: f.select_type_text,
                        role_name: f.role_name,
                        selected_field: f.selected_field,
                        main_uuid: f.uuid,
                        type: f.type
                    })
                );

            result.push(originalField);
        });
        return result;
    }

    private syncMatchingConditionalFields(fieldData: Field) {
        const matchingFields = this.getMatchingCurrentFields(fieldData).filter(f => f.uuid !== fieldData.uuid);
        matchingFields.forEach(matchingField => {
            this.globalService.updateField(matchingField.uuid, {
                connected_parent_conditional_id: fieldData.connected_parent_conditional_id,
                enable_conditional: fieldData.enable_conditional,
            });
            fieldData.connected_parent_conditional_id.forEach(field => this.addLinkedConditional(field, matchingField.uuid));
            applyRelationsOnCurrent(matchingField.uuid, this.globalService, true);
        });
    }
}